import { SNETButton, SNETTextfield, StyledDropdown } from "shared/dist/components";
import PropTypes from "prop-types";
import RemoveButton from "./RemoveButton";
import "./style.css";
import { useTheme } from "@mui/material";
import { alertTypes } from "shared/dist/components/domainless/feedback/AlertBox";
import { isEqual } from "lodash";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

const MAX_AUTH_FIELDS = 5;
const ON_CHANGE_DEBOUNCE_MS = 200;
const AUTHORIZATION_LOCATION_OPTIONS = [
    { value: "header", label: "Header" },
    { value: "query", label: "Query" },
    { value: "body", label: "Body" },
];

let authorizationFieldIdCounter = 0;

const createFieldId = () => `auth-field-${authorizationFieldIdCounter++}`;

const getComparableAuthorizationField = ({ key, value, location }) => ({
    key,
    value,
    location,
});

const normalizeOriginalAuthorizationField = (field) => ({
    id: createFieldId(),
    key: field.key || "",
    value: field.value || "",
    location: field.location || "header",
});

const createNewAuthorizationField = () => ({
    id: createFieldId(),
    key: "",
    value: "",
    location: "header",
    keyTouched: false,
    valueTouched: false,
});

const getValidation = (value) => {
    if (!value) {
        return { type: alertTypes.ERROR, message: "must not be empty!" };
    }
    return undefined;
};

const hasAuthorizationFieldError = (field) =>
    Boolean(getValidation(field.key) || getValidation(field.value));

const getVisibleFieldError = (field, fieldName) =>
    field[`${fieldName}Touched`] ? getValidation(field[fieldName]) : undefined;

const OldAuthorizationFieldRow = memo(({ field, inputSx, onRemove }) => {
    const handleRemove = useCallback(() => onRemove(field.id), [field.id, onRemove]);

    return (
        <div className="authorization-field-row">
            <SNETTextfield
                type="text"
                name="key"
                label="Key"
                value={field.key}
                className="authorization-field-input"
                sx={inputSx}
            />
            <SNETTextfield
                type="password"
                name="value"
                label="Value"
                value={field.value.substring(0, 20)}
                className="authorization-field-input"
                sx={inputSx}
            />
            <SNETTextfield
                type="text"
                name="location"
                label="Location"
                value={field.location}
                className="authorization-field-input"
                sx={inputSx}
            />
            <RemoveButton onRemove={handleRemove} />
        </div>
    );
});

OldAuthorizationFieldRow.propTypes = {
    field: PropTypes.object.isRequired,
    inputSx: PropTypes.object.isRequired,
    onRemove: PropTypes.func.isRequired,
};

const NewAuthorizationFieldRow = memo(({ field, inputSx, onChange, onBlur, onRemove }) => {
    const handleKeyChange = useCallback(
        (event) => onChange(field.id, event.target.name, event.target.value),
        [field.id, onChange]
    );
    const handleValueChange = useCallback(
        (event) => onChange(field.id, event.target.name, event.target.value),
        [field.id, onChange]
    );
    const handleLocationChange = useCallback(
        (event) => onChange(field.id, "location", event.target.value),
        [field.id, onChange]
    );
    const handleKeyBlur = useCallback(() => onBlur(field.id, "key"), [field.id, onBlur]);
    const handleValueBlur = useCallback(() => onBlur(field.id, "value"), [field.id, onBlur]);
    const handleRemove = useCallback(() => onRemove(field.id), [field.id, onRemove]);

    return (
        <div className="authorization-field-row">
            <SNETTextfield
                type="text"
                name="key"
                label="Key"
                value={field.key}
                onChange={handleKeyChange}
                onBlur={handleKeyBlur}
                placeholder="Key"
                className="authorization-field-input"
                error={getVisibleFieldError(field, "key")}
                sx={inputSx}
            />
            <SNETTextfield
                type="text"
                name="value"
                label="Value"
                value={field.value}
                onChange={handleValueChange}
                onBlur={handleValueBlur}
                placeholder="Value"
                className="authorization-field-input"
                error={getVisibleFieldError(field, "value")}
                sx={inputSx}
            />
            <StyledDropdown
                name="location"
                inputLabel="Location"
                value={field.location}
                list={AUTHORIZATION_LOCATION_OPTIONS}
                onChange={handleLocationChange}
                className="authorization-field-select"
            />
            <RemoveButton onRemove={handleRemove} />
        </div>
    );
});

NewAuthorizationFieldRow.propTypes = {
    field: PropTypes.object.isRequired,
    inputSx: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
};

const AuthorizationFieldsEditor = ({ originalAuthorizationFields, onChange }) => {
    const theme = useTheme();
    const normalizedOriginalFields = useMemo(
        () => originalAuthorizationFields.map(normalizeOriginalAuthorizationField),
        [originalAuthorizationFields]
    );
    const comparableOriginalFields = useMemo(
        () => normalizedOriginalFields.map(getComparableAuthorizationField),
        [normalizedOriginalFields]
    );
    const [retainedOriginalFields, setRetainedOriginalFields] = useState(normalizedOriginalFields);
    const [newFields, setNewFields] = useState([]);

    const inputSx = useMemo(
        () => ({
            "& .MuiInputBase-input": {
                color: theme.primitiveTokens.palette.main.black,
                letterSpacing: "0.15px",
            },
        }),
        [theme]
    );

    const currentAuthorizationFields = useMemo(
        () => [...retainedOriginalFields, ...newFields],
        [retainedOriginalFields, newFields]
    );
    const editedAuthorizationFields = useMemo(
        () => currentAuthorizationFields.map(getComparableAuthorizationField),
        [currentAuthorizationFields]
    );
    const hasError = useMemo(
        () => editedAuthorizationFields.some(hasAuthorizationFieldError),
        [editedAuthorizationFields]
    );
    const isChanged = useMemo(
        () => !isEqual(editedAuthorizationFields, comparableOriginalFields),
        [editedAuthorizationFields, comparableOriginalFields]
    );
    const isMaxLengthReached = retainedOriginalFields.length + newFields.length >= MAX_AUTH_FIELDS;

    useEffect(() => {
        setRetainedOriginalFields(normalizedOriginalFields);
        setNewFields([]);
    }, [normalizedOriginalFields]);

    const editorState = useMemo(
        () => ({
            fields: editedAuthorizationFields,
            hasError,
            isChanged,
        }),
        [editedAuthorizationFields, hasError, isChanged]
    );

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            onChange(editorState);
        }, ON_CHANGE_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [editorState, onChange]);

    const handleAddField = useCallback(() => {
        setNewFields((previousFields) => [...previousFields, createNewAuthorizationField()]);
    }, []);

    const handleRemoveOriginalField = useCallback((fieldId) => {
        setRetainedOriginalFields((previousFields) =>
            previousFields.filter((field) => field.id !== fieldId)
        );
    }, []);

    const handleRemoveNewField = useCallback((fieldId) => {
        setNewFields((previousFields) => previousFields.filter((field) => field.id !== fieldId));
    }, []);

    const handleFieldChange = useCallback((fieldId, fieldName, value) => {
        setNewFields((previousFields) =>
            previousFields.map((field) =>
                field.id === fieldId
                    ? {
                          ...field,
                          [fieldName]: value,
                          ...(fieldName === "key" || fieldName === "value"
                              ? { [`${fieldName}Touched`]: true }
                              : {}),
                      }
                    : field
            )
        );
    }, []);

    const handleFieldBlur = useCallback((fieldId, fieldName) => {
        setNewFields((previousFields) =>
            previousFields.map((field) =>
                field.id === fieldId ? { ...field, [`${fieldName}Touched`]: true } : field
            )
        );
    }, []);

    return (
        <div className="authorization-fields-editor-container">
            {retainedOriginalFields.map((field) => (
                <OldAuthorizationFieldRow
                    key={field.id}
                    field={field}
                    inputSx={inputSx}
                    onRemove={handleRemoveOriginalField}
                />
            ))}
            {newFields.map((field) => (
                <NewAuthorizationFieldRow
                    key={field.id}
                    field={field}
                    inputSx={inputSx}
                    onChange={handleFieldChange}
                    onBlur={handleFieldBlur}
                    onRemove={handleRemoveNewField}
                />
            ))}
            <SNETButton
                type="button"
                color="primary"
                variant="outlined"
                onClick={handleAddField}
                className="add-field-button"
                disabled={isMaxLengthReached}
            >
                + Add Field{" "}
                {isMaxLengthReached
                    ? "(The maximum number of fields has been reached. To add or replace a field, delete the existing one.)"
                    : ""}
            </SNETButton>
        </div>
    );
};

export default AuthorizationFieldsEditor;

AuthorizationFieldsEditor.propTypes = {
    originalAuthorizationFields: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
};
